import { useState, useEffect, useMemo, useCallback, useReducer } from "react";
import { useNavigate } from "react-router";
import { toast } from "@/components/Toaster";
import { useAuth } from "@/hooks/useAuth";
import { usePoll } from "@/hooks/usePoll";
import { REMITTANCE_STEP_TYPES } from "@capture/shared";
import { useQueues, type QueueSummary, type LaneInstruction } from "../hooks/useQueues";
import { useEscalationAlerts } from "../hooks/useEscalationAlerts";
import { useFeedLock } from "../hooks/useFeedLock";
import { useMyEvictions } from "@/features/queue-evictions/hooks/useMyEvictions";
import { evictionRefusalNotice } from "@/features/queue-evictions/api/client";
import { StepSection } from "../components/StepSection";
import { ReconciledSummaryBar } from "../components/ReconciledSummaryBar";
import { TerminalLaneSection } from "../components/TerminalLaneSection";
import { EscalationQueue, type EscalatedDocument } from "../components/EscalationQueue";
import { EscalationContextPanel } from "../components/EscalationContextPanel";
import { BatchContextBanner } from "../components/BatchContextBanner";
import { UploadDialog } from "@/features/batches/components/UploadDialog";
import { useJobTypes } from "@/features/admin/hooks/useJobTypes";
import { EmptyState } from "@/components/EmptyState";
import { api } from "@/lib/api-client";
import { queueBlocksApi } from "@/features/admin/queue-blocks/api/client";
import { InstructionDialog } from "@/features/admin/queue-instructions/components/InstructionDialog";
import { useWsClient } from "@/lib/ws-provider";
import { resolveFeedRoute, remittanceStationRoute } from "../lib/feed-route";
import { PendingReleaseIndicator } from "../components/PendingReleaseIndicator";
import { usePendingRelease } from "@/features/pending-release/hooks/usePendingRelease";
import "../queues.css";

interface CurrentDocument {
  id: string;
  filename: string;
  currentStep: string;
  assignedAt: string;
}

export function QueueDashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const wsClient = useWsClient();
  const { queues, reconciled, isLoading, isRefreshing, error, refetch } = useQueues(wsClient);
  // WU-1.5 — live ESCALATION_NEW consumer: permitted users get a toast and
  // the escalation panel re-fetches (badge bump) via the reload token. The
  // hook warms + exposes the `escalation_resolve` permission, which also
  // gates the EscalationQueue panel below (one check, two consumers — the
  // page previously ran its own duplicate /permissions/check on mount).
  const [escalationReloadToken, bumpEscalationReload] = useReducer(
    (n: number) => n + 1,
    0,
  );
  const { permitted: showEscalation } = useEscalationAlerts(wsClient, {
    onEscalation: bumpEscalationReload,
  });
  // R-182 / R-202 — refresh hygiene: stale-while-revalidate in useQueues keeps
  // the cards mounted across refetches (no flicker). As of R-202 the web app
  // mounts a live WsClient via WsProvider, so useQueues' WS subscriptions
  // (queue_update / queue_block / queue_unblock / document_advance) are now the
  // ACTIVE refresh path. This poll is the reconnect safety net at 30s — it
  // covers the window while the socket is dropped/backing off. Pauses when the
  // tab is hidden.
  usePoll(refetch, 30_000);
  // R-182 — initial-load skeleton only. `isLoading` is one-way, so gate the
  // spinner on data-presence so a background refetch keeps the cards mounted.
  const showInitialSkeleton = isLoading && queues.length === 0;
  const { items: jobTypes, isLoading: jobTypesLoading } = useJobTypes();
  // R-162 — operator's continuous-feed lock (server-side Redis, 60s rolling TTL).
  const { lock: feedLock, start: startFeed } = useFeedLock();
  // WU-2.4 T7 — the viewing operator's own active evictions; lane-locks the
  // matching StepSection cards and suppresses their start-feed affordance.
  const { evictions: myEvictions } = useMyEvictions(wsClient);
  // F-046 — discoverability: surface the count of batches held for supervisor
  // release so an operator on the dashboard is pointed to Pending Release.
  const { groups: pendingReleaseGroups } = usePendingRelease();

  const [currentDoc, setCurrentDoc] = useState<CurrentDocument | null>(null);
  const [selectedEscalation, setSelectedEscalation] = useState<EscalatedDocument | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);

  // Load current assigned document
  useEffect(() => {
    if (!user) return;

    api
      .get<{ documents: CurrentDocument[]; total: number }>(
        "/queues/key?limit=1",
      )
      .catch(() => null)
      .then(() => {
        // If user has a locked document, we need a different approach:
        // Check the locks endpoint for the current user's active lock
        return api.get<{ locks: Array<{
          documentId: string;
          operatorId: string;
          acquiredAt: string;
        }> }>("/locks");
      })
      .then((lockData) => {
        if (!lockData) return;
        const myLock = lockData.locks.find(
          (l) => l.operatorId === user.userId,
        );
        if (myLock) {
          // Fetch basic document info
          api
            .get<{
              id: string;
              filename: string;
              currentStep: string;
              assignedAt: string;
            }>(`/documents/${myLock.documentId}`)
            .then((doc) => {
              setCurrentDoc({
                id: doc.id,
                filename: doc.filename,
                currentStep: doc.currentStep,
                assignedAt: myLock.acquiredAt,
              });
            })
            .catch(() => {
              // Document no longer available
            });
        }
      })
      .catch(() => {
        // No locks endpoint or no locks — that's fine
      });
  }, [user]);

  // R-162 — canonical step list. Render every one, even with totalCount=0,
  // so a stuck stage shows as a non-zero count that doesn't decrement
  // instead of vanishing entirely (design §5.1).
  const CANONICAL_STEPS = useMemo(
    // F-039 — lanes key on pipeline stepType ("qc"), not the stage word ("qa"):
    // no pipeline ever emits a "qa" step, so that lane sat at zero forever while
    // real qc summaries were silently dropped by canonical-only rendering.
    () => ["scan", "classify", "key", "verify", "qc", "supervisor", "export"] as const,
    [],
  );

  // Group queues by step type
  const groupedQueues = useMemo(() => {
    const groups = new Map<string, QueueSummary[]>();
    for (const q of queues) {
      const existing = groups.get(q.stepType) ?? [];
      existing.push(q);
      groups.set(q.stepType, existing);
    }
    return groups;
  }, [queues]);

  // R-216 — one collapsible StepSection per canonical step. Merge all API
  // summaries for a step into a single section (combine lanes + doc totals) so
  // each step renders exactly one collapsible header. Synthesize a zeroed
  // summary for any canonical step missing from the API response so all 7
  // sections render unconditionally (always-show-zero; design §5.1, R-162).
  //
  // F-011 — remittance step types (SP3, the shared REMITTANCE_STEP_TYPES
  // catalog) are job-type-conditional, NOT canonical: they render when (and
  // only when) the API returns a summary for them, spliced after Classify (they
  // occupy the keying slot in remittance pipelines). Without this the API's
  // remittance lane — batch + transaction counts — was silently dropped and the
  // remittance job only surfaced under the standard Key lane.
  const stepsToRender = useMemo<QueueSummary[]>(() => {
    const displaySteps: string[] = CANONICAL_STEPS.flatMap((stepType) =>
      stepType === "classify"
        ? [stepType, ...REMITTANCE_STEP_TYPES.filter((s) => groupedQueues.has(s))]
        : [stepType],
    );
    return displaySteps.map((stepType) => {
      const existing = groupedQueues.get(stepType);
      if (!existing || existing.length === 0) {
        return { stepType, totalCount: 0, byDocumentType: [], byJobType: [] };
      }
      // Combine lanes + totals across all summaries for this step. F-011 — seed
      // the reduce with the FIRST summary (spread) and spread `...acc` in the
      // combiner so header passthrough scalars (remittanceSummary,
      // awaitingRelease, stepInstruction, and any field added later) survive by
      // default with first-wins semantics; only the genuinely-combinable
      // list/count fields are enumerated. The earlier empty-seed rebuild
      // silently dropped every unenumerated field — the F-011 bug.
      // No seed: reduce returns existing[0] verbatim for the single-summary
      // case (the norm) — every field survives; multi-summary steps combine
      // via the spread below.
      return existing.reduce((acc, q) => ({
        ...acc,
        stepType,
        totalCount: acc.totalCount + q.totalCount,
        byDocumentType: [...acc.byDocumentType, ...q.byDocumentType],
        byJobType: [...acc.byJobType, ...q.byJobType],
        assignedOperators: [...(acc.assignedOperators ?? []), ...(q.assignedOperators ?? [])],
        awaitingRelease: (acc.awaitingRelease ?? 0) + (q.awaitingRelease ?? 0),
        remittanceSummary:
          acc.remittanceSummary || q.remittanceSummary
            ? {
                batchCount: (acc.remittanceSummary?.batchCount ?? 0) + (q.remittanceSummary?.batchCount ?? 0),
                transactionCount:
                  (acc.remittanceSummary?.transactionCount ?? 0) + (q.remittanceSummary?.transactionCount ?? 0),
              }
            : undefined,
      }));
    });
  }, [CANONICAL_STEPS, groupedQueues]);

  // R-216 C3 — job-name / batches / docs click → Batches filtered by the lane's
  // (step, jobType). The Batches screen reads these query params (Task D2).
  // F-031 Phase 3 — "error" and "complete" are terminal queue states, not
  // pipeline step types. BatchListPage filters `currentStep.primary` against
  // the step param; passing step=error or step=complete would produce a
  // client-side no-match (empty list) even when jobType has results. Omit
  // the step param for terminal lanes so only the jobType filter applies.
  // (Declared before handleStartFeed, which reuses it as its empty-claim
  // fallback.)
  const handleNavigateToBatches = useCallback(
    (stepType: string, jobTypeId: string) => {
      const isTerminal = stepType === "error" || stepType === "complete";
      // F-047 — terminal lanes deep-link with `state=` (a doc-state, not a
      // pipeline step) so BatchListPage filters to batches containing docs in
      // that state; `step=` would client-side no-match (error/complete aren't
      // step types) and show an empty list.
      const params = isTerminal
        ? `state=${encodeURIComponent(stepType)}&jobType=${encodeURIComponent(jobTypeId)}`
        : `step=${encodeURIComponent(stepType)}&jobType=${encodeURIComponent(jobTypeId)}`;
      navigate(`/batches?${params}`);
    },
    [navigate],
  );

  // R-162 / E-051 — the per-lane "Claim next" button starts the operator feed
  // for this lane. F-041 — route by step: a classify lane opens the
  // Classification Station, every other lane opens the Verification Station.
  const handleStartFeed = useCallback(
    async (stepType: string, jobTypeId: string) => {
      const result = await startFeed(stepType, jobTypeId);
      // SP3 — remittance lanes claim a batch and open the remittance station.
      if (result.available && result.batch) {
        navigate(remittanceStationRoute(result.batch.id));
        return;
      }
      if (result.available && result.document) {
        navigate(resolveFeedRoute(stepType, result.document.documentId));
        return;
      }
      // F-067 — available:false used to be a SILENT no-op ("count drift on
      // the next poll will tell the operator"). With a stale lane there is
      // no drift and the click is just dead. An explicit click must always
      // visibly do something: say why, then fall back to browsing the lane's
      // batches (the same navigation the name click performs).
      // WU-2.4 T7 (relocated by E-051) — when the refusal is an eviction,
      // surface the specific reason instead of the generic empty message.
      toast.info(
        evictionRefusalNotice(result) ??
          "Nothing available to pick up right now — opening the batches instead.",
      );
      handleNavigateToBatches(stepType, jobTypeId);
    },
    [startFeed, navigate, handleNavigateToBatches],
  );

  // E-057 follow-on — the lane's "N docs failed" badge counts export-error
  // docs across ALL batches of the job type, so its click-through lands on
  // Export History scoped the same way (job type + dead-letter records).
  const handleShowExportFailures = useCallback(
    (jobTypeId: string) => {
      navigate(
        `/admin/export-history?jobTypeId=${encodeURIComponent(jobTypeId)}&status=dead_letter`,
      );
    },
    [navigate],
  );

  // R-216 C3/C4 — supervisor lock-cell click toggles the queue block via the
  // existing /api/admin/queue-blocks endpoint (POST to block, DELETE to clear).
  const handleToggleLock = useCallback(
    async (stepType: string, jobTypeId: string, currentlyBlocked: boolean) => {
      try {
        if (currentlyBlocked) {
          const { blocks } = await queueBlocksApi.list();
          const active = blocks.find(
            (b) =>
              b.stepType === stepType &&
              b.jobTypeId === jobTypeId &&
              b.clearedAt === null,
          );
          if (active) await queueBlocksApi.delete(active.id);
        } else {
          await queueBlocksApi.create({
            stepType,
            jobTypeId,
            reason: "Blocked from queue dashboard",
          });
        }
        await refetch();
      } catch {
        // The WS queue_block/queue_unblock event (or the 30s poll) will
        // reconcile; a transient failure here is non-fatal to the dashboard.
      }
    },
    [refetch],
  );

  // R-216 C4 — lock toggle is gated on supervisor.queue_block (session perm).
  const canToggleLock = user?.permissions?.includes("supervisor.queue_block") ?? false;

  // WU-2.2 — instruction affordance gated on supervisor.queue_instruct.
  const canInstruct = user?.permissions?.includes("supervisor.queue_instruct") ?? false;
  const [instructionTarget, setInstructionTarget] = useState<{
    stepType: string; jobTypeId: string; jobTypeName: string;
    current: LaneInstruction | null;
  } | null>(null);

  const handleEscalationResolved = useCallback(() => {
    setSelectedEscalation(null);
    refetch();
  }, [refetch]);

  function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    return `${hours}h ago`;
  }

  return (
    <div className="queue-dashboard form-default">
      <div className="queue-header">
        <h1 className="queue-title">Queue Dashboard</h1>
        {/* E-051 — the page-top "Get Next Document" button is retired: it was
            silently scoped to the KEY queue only, misrepresenting the model
            (every step is a queue; none is privileged). Claiming is the
            per-lane "Claim next" button on every feedable lane. */}
        <PendingReleaseIndicator count={pendingReleaseGroups.length} />
      </div>

      <ReconciledSummaryBar reconciled={reconciled} />

      <BatchContextBanner />

      {/* WU-2.4 T7 / E-051 — a refused claim must never be a silent dead end.
          Per-lane claims run through handleStartFeed, whose available:false
          fallback (F-067) toasts the reason and browses to the lane's
          batches; eviction locks render inline on the lane (myEvictions).
          This banner carries queue-load errors. */}
      {error && (
        <div className="queue-error">
          {error}
        </div>
      )}

      {showInitialSkeleton && (
        <div className="queue-loading">
          <div className="loading-spinner" />
        </div>
      )}

      {/* R-182 — subtle non-blocking background-refresh cue. Never unmounts the
          step cards or shifts layout. */}
      {isRefreshing && queues.length > 0 && (
        <div className="queue-refreshing" role="status" aria-live="polite">
          Refreshing…
        </div>
      )}

      {/* R-162 — only show the "no job types yet" bootstrap empty state when
          the tenant has literally no job types. When job types exist but the
          queue is empty, the canonical step cards (each with "No documents
          currently") render below — that IS the monitor view. No need to
          stack an EmptyState on top. */}
      {!showInitialSkeleton &&
        !jobTypesLoading &&
        jobTypes.length === 0 &&
        !error && (
          <EmptyState
            title="No job types yet"
            body="No documents in any queue. Create a job type before uploading your first batch."
            action={{
              label: "Create Job Type",
              onClick: () => navigate("/admin/job-types"),
            }}
          />
        )}

      {!showInitialSkeleton &&
        stepsToRender.map((stepQueue) => (
          <StepSection
            key={stepQueue.stepType}
            queue={stepQueue}
            activeFeedLock={feedLock}
            myEvictions={myEvictions}
            // F-051 — `export` is automated delivery, not operator keying. Passing
            // no feed handler makes the lane non-feedable (JobTypeRow disables the
            // ready cell and the name-click falls through to Batches) so an
            // operator can't claim a mid-delivery doc into the Verification Station.
            onStartFeed={stepQueue.stepType === "export" ? undefined : handleStartFeed}
            onNavigateToBatches={handleNavigateToBatches}
            onShowExportFailures={handleShowExportFailures}
            onToggleLock={handleToggleLock}
            canToggleLock={canToggleLock}
            canInstruct={canInstruct}
            onEditInstruction={(stepType, jobTypeId, current) => {
              const lane = stepQueue.byJobType.find((l) => l.jobTypeId === jobTypeId);
              setInstructionTarget({
                stepType, jobTypeId,
                jobTypeName: lane?.jobTypeName ?? jobTypeId,
                current,
              });
            }}
          />
        ))}

      {/* F-031 Phase 3 — terminal lanes (Needs attention / Complete). Sourced
          from the RAW `queues` array (not stepsToRender, which is
          CANONICAL_STEPS-only) so terminal summaries never enter the operator
          step grid. error renders first (higher urgency), then complete. */}
      {!showInitialSkeleton &&
        ["error", "complete"].map((st) => {
          const summary = queues.find((q) => q.stepType === st);
          return summary ? (
            <TerminalLaneSection
              key={st}
              summary={summary}
              onNavigateToBatches={handleNavigateToBatches}
            />
          ) : null;
        })}

      {/* Escalation section — only for users with permission */}
      <EscalationQueue
        visible={showEscalation}
        onSelectDocument={setSelectedEscalation}
        reloadToken={escalationReloadToken}
      />

      {/* Current document indicator */}
      {currentDoc && (
        <div className="queue-current">
          <div className="queue-current-label">Your Current Document</div>
          <div className="queue-current-row">
            <span className="queue-current-name">{currentDoc.filename}</span>
            <span className="queue-current-step">{currentDoc.currentStep}</span>
            <span className="queue-current-time">
              Locked {timeAgo(currentDoc.assignedAt)}
            </span>
            <button
              className="queue-current-open"
              onClick={() => navigate(`/verify/${currentDoc.id}`)}
            >
              Open
            </button>
          </div>
        </div>
      )}

      {/* Escalation context panel */}
      {selectedEscalation && (
        <EscalationContextPanel
          document={selectedEscalation}
          onClose={() => setSelectedEscalation(null)}
          onResolved={handleEscalationResolved}
        />
      )}

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />

      {instructionTarget && (
        <InstructionDialog
          fixedScope={{
            stepType: instructionTarget.stepType,
            jobTypeId: instructionTarget.jobTypeId,
            scopeLabel: `${instructionTarget.stepType} / ${instructionTarget.jobTypeName}`,
          }}
          initialBody={instructionTarget.current?.body ?? null}
          existingId={instructionTarget.current?.id ?? null}
          onClose={() => setInstructionTarget(null)}
          onSaved={() => void refetch()}
        />
      )}
    </div>
  );
}
